import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  BarChart3, 
  UserPlus,
  ClipboardList,
  DoorOpen,
  Mail,
  Calendar
} from 'lucide-react';
import DepartmentEmailModule from './DepartmentEmailModule';
import UsherRotaSection from './UsherRotaSection';

const USHER_TABS = [
  { id: 'attendance', label: 'Attendance', icon: BarChart3, color: 'from-blue-500 to-cyan-500' },
  { id: 'manual', label: 'Manual Entry', icon: UserPlus, color: 'from-orange-500 to-amber-500' },
  { id: 'sunday_rota', label: 'Sunday Rota', icon: Calendar, color: 'from-violet-600 to-purple-600' },
  { id: 'email', label: 'Email', icon: Mail, color: 'from-indigo-500 to-purple-500' }
];

const UsherDepartmentSection = ({ 
  adminUser, 
  renderAttendance,
  renderManualEntry
}) => {
  const [activeTab, setActiveTab] = useState('attendance');

  return (
    <div className="space-y-6" data-testid="usher-department-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white shadow-xl"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="p-3 rounded-xl bg-white/20 backdrop-blur-md w-fit">
            <DoorOpen className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold">Usher Department</h2>
            <p className="text-orange-100 text-sm sm:text-base">Manage attendance tracking and visitor entries</p>
          </div>
        </div>
      </motion.div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto">
          {USHER_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'attendance' && (
          <motion.div
            key="attendance"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderAttendance()}
          </motion.div>
        )}
        {activeTab === 'manual' && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderManualEntry()}
          </motion.div>
        )}
        {activeTab === 'sunday_rota' && (
          <motion.div
            key="sunday_rota"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <UsherRotaSection />
          </motion.div>
        )}
        {activeTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DepartmentEmailModule department="usher" adminUser={adminUser} compact={false} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UsherDepartmentSection;
